import type { PayloadAction } from "@reduxjs/toolkit";
import {
  filterAddresses,
  parseCustomerRecord,
  stripProviderOwnedFields,
} from "../../../helpers/customerHelpers";

/**
 * Shared parts of CustomerSlice — plan §5.2. 12 of ~20 reducers are shared.
 *
 * ⚠️ **That number was 7 until mobile's dead `isCustomerDataFetching` flag was removed.**
 * The field was WRITTEN by nine mobile reducers and READ by nothing anywhere in
 * `apps/mobile` — verified by grep across the whole app. Deleting it made five of those
 * reducers byte-identical to web's, so they moved here. This is the one place the uplift
 * deliberately changed mobile's state shape; see the ledger.
 *
 * 🔴 **`getCustomerRecord` and `setCustomerDataSuccess` converged on 2026-09-05** and are below.
 * They were the last parse divergence: web dropped `providerConsents` and merged non-external
 * `loyaltyInfo` but never filtered addresses; mobile filtered addresses but kept the
 * provider-owned fields on the customer record. **Each half was doing something the other
 * needed**, so the shared bodies do BOTH rather than picking a winner:
 *   - mobile gains web's parse (see the note on `getCustomerRecord` for why that matters now);
 *   - web gains the empty-address filter, which is why that is called out in the doc — an
 *     address with no `addressLine` used to render as a selectable radio with no label.
 * (`getCustomerDetailsUsingMobile` and `resetCustomerStore` used to be listed here as
 * divergent. They no longer are: the only thing that differed was mobile not clearing
 * `externalLoyaltyData`, which it must now do too, so both are byte-identical and live below.)
 *
 * 🔴 **`externalLoyaltyData` stopped being web-only on 2026-09-05**, when mobile took the
 * `packages/core/loyalty` consent flow. It is the single source of truth for the current
 * customer's `loyaltyInfo` and `providerConsents`, and all three consent selectors read it —
 * without it every mobile customer looks like a pre-consent customer and the enrolment block
 * never renders.
 *
 * `authenticateCustomerDataSuccess` IS shared, even though the apps spell the property access
 * differently — web `customerAuthenticationData["otpHash"]`, mobile `.otpHash`. Same
 * operation. `uplift-analyse` now canonicalises `obj["k"]` to `obj.k` when the key is a valid
 * identifier, so the gate accepts either spelling; see the ledger's normalisation table.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface SharedCustomerState {
  customerInfo: {
    [key: string]: any
  },
  customerSummary: {
    [key: string]: any
  },
  isAuthenticateCustomerDataFetching: boolean,
  customerAuthenticationData: any,
  isCustomerDataNotFound: boolean,
  resetCustomerNum: boolean,
  /**
   * The current customer's loyalty + consent state as the PROVIDER sees it, or `null`.
   *
   * `customer` is populated only when the customer is absent from our DB but exists at the
   * external loyalty provider — used to prefill the new-customer form.
   *
   * ⚠️ `customerRefId` says WHOSE this is, and it is not decoration: this object outlives a
   * customer swap, so a caller asking "do we have loyalty info yet?" gets `true` for the
   * PREVIOUS customer without it. Read it through `selectCustomerLoyaltyRefId`
   * (`packages/core/loyalty/selectors`); `ensureCustomerLoyaltyInfo` is what depends on it.
   *
   * Stamped only on the internal-ref fetch path — an external fetch keys on the customer's phone
   * number, which is not a `customerRefId`, so that path leaves it `undefined`.
   */
  externalLoyaltyData: {
    loyaltyInfo: any;
    providerConsents: any;
    customer?: any;
    customerRefId?: string;
  } | null,
}

export const sharedInitialState: SharedCustomerState = {
  customerInfo: {},
  customerSummary: {},
  isAuthenticateCustomerDataFetching: false,
  customerAuthenticationData: {},
  isCustomerDataNotFound: false,
  resetCustomerNum: false,
  externalLoyaltyData: null,
};

/** Identical in both apps. */
export const sharedReducers = {
  getCustomerData: (state: SharedCustomerState) => {

  },
  getCustomerDataFail: (state: SharedCustomerState) => {

  },
  getCustomerSummary: (state: SharedCustomerState, action: PayloadAction<any>) => {
  },
  getCustomerSummaryRecord: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.customerSummary = action.payload;
  },
  authenticateCustomerData: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.isAuthenticateCustomerDataFetching = true;
  },
  authenticateCustomerDataSuccess: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.isAuthenticateCustomerDataFetching = false;
    state.customerAuthenticationData.otpHash = action.payload?.hash;
  },
  authenticateCustomerDataFailure: (state: SharedCustomerState) => {
    state.isAuthenticateCustomerDataFetching = false;
  },
  customerRecordNotFound: (state: SharedCustomerState) => {
    state.customerInfo = {};
    state.isCustomerDataNotFound = true;
  },
  resetCustomerRecordNotFound: (state: SharedCustomerState) => {
    state.isCustomerDataNotFound = false;
  },
  toggleCustomerNum: (state: SharedCustomerState) => {
    state.resetCustomerNum = !state.resetCustomerNum;
  },
  saveCustomerInfo: (state: SharedCustomerState, action: PayloadAction<any>) => { },
  getDetailedCustomerInfo: (state: SharedCustomerState, action: PayloadAction<any>) => {},
  /** Shared since 2026-09-05 — the customer saga writes it on both platforms. */
  setExternalLoyaltyData: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.externalLoyaltyData = action.payload ?? null;
  },
  /**
   * A new lookup invalidates the previous customer's provider view. Without the clear, the
   * consent block seeds its toggle from the LAST customer's `providerConsents`.
   */
  getCustomerDetailsUsingMobile: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.externalLoyaltyData = null;
  },
  /**
   * The customer record, from a search result or a detail fetch.
   *
   * 🔴 **`parseCustomerRecord` prefers the PAYLOAD's `redemptionScheme` / `membershipName`**,
   * falling back to the record we already hold. Mobile's own version read the stored value
   * unconditionally, which was harmless only while its payload never carried those fields —
   * the shared `getCustomerInfo` saga now attaches them from the filter result before
   * dispatching, so the old form would have discarded the fresh values for stale ones.
   *
   * ⚠️ `filterAddresses` came from mobile and is NOT optional here: `getUniqueAddresses` only
   * dedupes, so without this an address with an empty `addressLine` reaches the address list.
   */
  getCustomerRecord: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.customerInfo = filterAddresses(
      parseCustomerRecord(action.payload, state.customerInfo),
    );
    state.isCustomerDataNotFound = false;
  },
  /** The customer record set directly by a screen, rather than fetched. */
  setCustomerDataSuccess: (state: SharedCustomerState, action: PayloadAction<any>) => {
    state.customerInfo = filterAddresses(stripProviderOwnedFields(action.payload));
  },
  resetCustomerStore: (state: SharedCustomerState) => {
    state.customerInfo = {};
    state.customerSummary = {};
    state.isAuthenticateCustomerDataFetching = false;
    state.customerAuthenticationData = {};
    state.isCustomerDataNotFound = false;
    state.externalLoyaltyData = null;
  },
};
